import PropTypes from 'prop-types'
import React from 'react'

import { connect } from 'react-redux'

import debounce from '@doveconviene/sf-web-js-sdk/debounce'

import styled from '@emotion/styled'

import { ReactComponent as DeleteIcon } from '@doveconviene/sf-ui/icons/close_circle.svg'
import { uxEvent } from '../../../../flux/actions/ux'
import { selectLabel } from '../../../../flux/selectors/i18n'
import { selectSearch } from '../../../../flux/selectors/ux'

import Icon from '../../../ui/Icon'

import { ReactComponent as SearchIcon } from '../../../../images/svg/search.svg'

import { selectIsSearchVisible } from '../../../../flux/selectors/layout'
import detectMobileBrowser from '../../../../utils/detectMobileBrowser'
import { useOnClickOutside } from '../../../ui/hooks/useOnClickOutside'

const Wrapper = styled.div(({ theme: { colors } }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px',
  borderRadius: '20px',
  backgroundColor: colors.white,
  border: `2px solid ${colors.primary}`,
  width: '100%',
}))

const StyledSearchIcon = styled.div(({ theme: { colors } }) => ({
  width: '22px',
  height: '22px',
  marginRight: '4px',
  color: colors.primary,
}))

const StyledDeleteIcon = styled.div(({ theme: { colors } }) => ({
  width: '22px',
  height: '22px',
  marginLeft: '4px',
  color: colors.mid_grey,
}))

const Input = styled.input(({ theme }) => ({
  flexGrow: '1',
  margin: 0,
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent',
  fontFamily: theme.fonts.primary,
  fontSize: theme.spaces.default * 0.875,
  fontWeight: '600',
  color: theme.colors.mid_grey,
  outline: 'none',
}))

const SearchFilter = ({
  mainRef,
  search,
  isSearchVisible,
  searchPlaceholder,
  onProductsSearchType,
}) => {
  const isMobileBrowser = detectMobileBrowser()

  const wrapperRef = React.useRef(null)
  const inputRef = React.useRef(null)

  const [value, setValue] = React.useState(search)

  useOnClickOutside(inputRef, () => {
    const { current: input } = inputRef
    input.blur()
  })

  const onInputChange = newValue => {
    setValue(newValue)
    onProductsSearchType(newValue)
  }

  const onInputKeyUp = e => {
    const { current: input } = inputRef

    // Enter
    if (input && e.keyCode === 13) input.blur()
  }

  const onInputFocus = () => {
    if (isMobileBrowser) {
      const { current: wrapper } = wrapperRef
      const { current: main } = mainRef

      if (wrapper && main) {
        wrapper.scrollIntoView()
      }
    }
  }

  const onDeleteIconClick = () => {
    const { current: input } = inputRef

    if (input) input.blur()
    onInputChange('')
  }

  React.useEffect(() => {
    setValue(search)
  }, [search])

  return isSearchVisible ? (
    <Wrapper ref={wrapperRef}>
      <StyledSearchIcon>
        <Icon>
          <SearchIcon />
        </Icon>
      </StyledSearchIcon>
      <Input
        type="text"
        data-test="SearchFilter"
        ref={inputRef}
        value={value}
        placeholder={searchPlaceholder}
        onChange={e => onInputChange(e.target.value)}
        onKeyUp={onInputKeyUp}
        onFocus={onInputFocus}
      />
      {value && (
        <StyledDeleteIcon data-test="ProductsSearchDeleteIcon" onClick={onDeleteIconClick}>
          <Icon>
            <DeleteIcon />
          </Icon>
        </StyledDeleteIcon>
      )}
    </Wrapper>
  ) : null
}

SearchFilter.propTypes = {
  search: PropTypes.string.isRequired,
  mainRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.shape({}),
    ]),
  }),
  isSearchVisible: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  onProductsSearchType: PropTypes.func,
}

SearchFilter.defaultProps = {
  isSearchVisible: true,
  mainRef: { current: null },
  searchPlaceholder: '',
  onProductsSearchType: () => false,
}

const mapStateToProps = state => ({
  search: selectSearch(state),
  isSearchVisible: selectIsSearchVisible(state),
  searchPlaceholder: selectLabel('searchPlaceholder')(state),
})

const mapDispatchToProps = {
  uxEvent,
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onProductsSearchType: debounce(search => {
    dispatchProps.uxEvent({
      name: 'type',
      target: 'ProductsSearch',
      params: {
        search,
      },
    })
  }, 200),
})

SearchFilter.displayName = 'SearchFilter'
SearchFilter.whyDidYouRender = true

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(SearchFilter)
